"use client";

import { AllShips } from "@/interfaces/IShips";
import { toKebabCase, toTitleCase } from "@/utils/helpers";
import {
  Container,
  Tab,
  Box,
  Typography,
  Divider,
  Button,
  Dialog,
  useMediaQuery,
  Pagination,
  Skeleton,
} from "@mui/material";
import Image from "next/image";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useSearchParams } from "next/navigation";
import { UniqueShip } from "@/interfaces/IUniqueShip";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
// import { MUITabs } from "../unique-ship/ShipAmenities";
import { ICruisesOperators } from "@/interfaces/ICruisesOperators";
import { getAllCruisesListV2, getAllShipData } from "@/services/backOffice";
import { MUITabs } from "@/components/common/MUITabs";
import Link from "next/link";

const ShipsByLines: React.FC<{
  allShips: AllShips;
  operators: ICruisesOperators;
  shipRef: string;
}> = ({ allShips, operators, shipRef }) => {
  const [allShpData, setAllShipData] = useState<AllShips>(allShips);
  const [isLoading, setIsLoading] = useState(true);

  const shipsByOperator = useMemo(
    () =>
      operators?.operatorDetails?.map((operator) => {
        const ships = allShpData?.ships?.filter((ship) => ship?.operator?.name === operator?.title);

        // ship style
        const style = ships
          ?.filter((item, index, self) => index === self.findIndex((x) => x.style === item.style))
          .map((x) => x.style);

        const shipStyle = style?.map((x) => ships?.filter((item) => item?.style === x));
        return { shipStyle, operator };
      }),

    [allShpData, operators],
  );

  const [tabValue, setTabValue] = useState<string>(shipsByOperator![0]?.operator?.id ?? "");
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    setPage(1);
  };

  const shipStyleSubText = (type: string) => {
    return type == "resort"
      ? "A floating paradise with endless entertainment, relaxation, and family-friendly fun."
      : type == "premium"
      ? "Elevated comfort and refined service for an unforgettable cruise experience."
      : type == "classic"
      ? "Timeless elegance meets modern convenience for a traditional cruising adventure."
      : type == "expedition"
      ? "Thrilling journeys to remote destinations with expert-led exploration."
      : type == "ultra_luxury"
      ? "The pinnacle of sophistication, offering exclusive experiences and personalized service."
      : type == "luxury"
      ? "Indulge in world-class amenities and impeccable hospitality on the high seas."
      : type == "premium_resort"
      ? "A seamless blend of resort-style leisure and high-end cruising excellence"
      : "";
  };

  const smokingArea = (type: string) => {
    return type == "some"
      ? "Smoking allowed in select areas."
      : type == "balcony_cabins"
      ? "Smoking allowed on balconies and in cabins"
      : type == "None"
      ? "No smoking allowed anywhere onboard."
      : "";
  };

  const adultOnlyText = (text: string) => {
    return text == "kids_allowed"
      ? "Kids allowed"
      : text == "adultsOnly" || text == "no_kids"
      ? "Kids not allowed"
      : "";
  };
  const scrollToTargetAdjusted = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element?.offsetTop - 86,
        behavior: "smooth",
      });
    }
  };

  const searchParams = useSearchParams();
  const cruiseLine = searchParams.get("cruise-line");
  const shipName = searchParams.get("ship");

  useEffect(() => {
    if (cruiseLine || shipName) {
      setTabValue(cruiseLine ?? "");
      setOperatorID(cruiseLine ?? "");
      scrollToTargetAdjusted("ship-container");
    } else if (!!cruiseLine) {
      setTabValue(cruiseLine);
    }
  }, [cruiseLine, shipName]);

  const [openImage, setOpenImage] = useState(false);
  const [getImageData, setGetImageData] = useState({
    image: "",
    title: "",
  });

  const scrollIntoView = () => {
    const element = document.getElementById("all-ships");
    if (element) {
      window?.scrollTo({
        top: element.offsetTop - 150,
        behavior: "smooth",
      });
    }
  };

  const [page, setPage] = React.useState(1);
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setIsLoading(true);
    scrollIntoView();
  };

  const [operatorID, setOperatorID] = useState(shipRef);
  useLayoutEffect(() => {
    const fetchData = async () => {
      if (page) {
        const { data } = await getAllShipData(8, page, operatorID);
        setAllShipData(data);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page, operatorID]);

  return (
    <Box id="ship-container">
      <Container>
        <Typography
          variant="h2"
          sx={{
            mt: { md: "1rem" },
            fontSize: { xs: "1.2rem", md: "1.5rem" },
            fontWeight: 500,
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
            color: "#060273",
            textAlign: "center",
            width: "100%",
          }}
        >
          Let&apos;s Pinpoint YOUR Ideal Best Cruise Ships!
        </Typography>

        <Typography
          sx={{
            color: "#060273",
            textAlign: "center",
            width: "100%",
          }}
        >
          Experience great pricing when you book luxury ships on the world&apos;s leading cruise
          lines with World Cruise Vibes. Get deals on the best cruise ships with free spending,
          upgrades, and more now.
        </Typography>
      </Container>
      <Box
        sx={{
          bgcolor: "#EBE0FF",
          p: { xs: "10px 0px", md: "14px 0px" },
          position: "sticky",
          top: "77px",
          zIndex: 5,
        }}
      >
        <Container
          sx={{
            py: "0px",
            ".MuiTabs-flexContainer": {
              justifyContent: "left !important",
            },
          }}
        >
          <MUITabs stringValue={tabValue} onChangeString={handleTabChange}>
            {operators?.operatorDetails?.map((item, index) => (
              <Tab
                key={index}
                iconPosition="start"
                sx={{
                  ...(tabValue === item?.id && {
                    bgcolor: "#20009e !important",
                    color: "#fff !important",
                  }),
                }}
                value={item?.id}
                label={
                  <Box
                    sx={{
                      textTransform: "initial",
                      fontWeight: 600,
                      fontSize: { xs: "15px", md: "15px" },
                      mb: "2px",
                      ml: { xs: "6px", md: "0px" },
                    }}
                  >
                    {toTitleCase(item?.title ?? "")}
                  </Box>
                }
                onClick={() => {
                  setTabValue(item?.id ?? "");
                  scrollToTargetAdjusted("ship-container");
                  setOperatorID(item?.id ?? "");
                  setIsLoading(true);
                  setPage(1);
                }}
              />
            ))}
          </MUITabs>
        </Container>
      </Box>
      <Container sx={{ py: "0px" }}>
        <Box id="all-ships">
          {operators?.operatorDetails
            ?.filter((x) => x?.id === tabValue)
            .map((item, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                    mt: { xs: "1rem", sm: "2rem" },
                  }}
                >
                  <Box
                    sx={{
                      height: "100px",
                      width: { xs: "100%", sm: "200px" },
                      position: "relative",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    <Image
                      src={item?.profileImageHref ?? ""}
                      alt={item?.title ?? ""}
                      fill
                      style={{
                        objectFit: "contain",
                      }}
                      loader={({ src }) => src}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "2rem", md: "3rem" },
                      fontWeight: 900,
                      fontFamily: "'blauerNue', 'blauerNue Fallback'",
                      color: "#060273",
                      textTransform: "uppercase",
                      width: { s: "100%", sm: "fit-content" },
                      textAlign: "center",
                    }}
                  >
                    {item?.title ?? ""}
                  </Typography>
                </Box>
              </Box>
            ))}

          <Divider sx={{ m: "1rem 0rem 2rem" }} />

          {isLoading ? (
            <Grid container spacing={2} size={{ xs: 12 }} sx={{ mb: "2.5rem" }}>
              {[...new Array(8)].map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ShipCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : (
            shipsByOperator
              ?.filter((x) => x?.operator?.id === tabValue)
              .map((item) =>
                item?.shipStyle?.map((style, styleIndex) => (
                  <Box key={styleIndex}>
                    <ShipStyle shipStyle={style ?? []} />

                    <Typography
                      sx={{
                        m: "0rem auto 2rem auto",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {shipStyleSubText(style![0]?.style ?? "")}
                    </Typography>

                    <Grid container spacing={2} size={{ xs: 12 }} sx={{ mb: "4rem" }}>
                      {style
                        ?.sort((a?: UniqueShip, b?: UniqueShip) => {
                          if (
                            toKebabCase(a?.title ?? "") === shipName &&
                            toKebabCase(b?.title ?? "") !== shipName
                          ) {
                            return -1;
                          }
                          if (
                            toKebabCase(b?.title ?? "") === shipName &&
                            toKebabCase(a?.title ?? "") !== shipName
                          ) {
                            return 1;
                          }
                          return 0;
                        })
                        .map((ship, shipIndex) => (
                          <Grid
                            id={toKebabCase(ship?.title ?? "") ?? ""}
                            key={shipIndex}
                            size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                          >
                            <ShipCard
                              ship={ship}
                              setOpenImage={setOpenImage}
                              setGetImageData={setGetImageData}
                              adultOnlyText={(text: string) => adultOnlyText(text)}
                              smokingArea={(type: string) => smokingArea(type)}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )),
              )
          )}
        </Box>
        <Pagination
          count={
            (allShpData?.total ?? 0) / 8 > 1 ? Number(((allShpData?.total ?? 0) / 8).toFixed()) : 1
          }
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          sx={{ width: "fit-content", m: "auto" }}
        />
        {openImage && (
          <Gellary
            open={openImage}
            setModalOpen={setOpenImage}
            coverImageHref={getImageData?.image}
            title={getImageData?.title}
          />
        )}
      </Container>
    </Box>
  );
};
export default ShipsByLines;

export const Gellary: React.FC<{
  open: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  coverImageHref: string;
  title: string;
  isDeck?: boolean;
  children?: React.ReactNode;
}> = ({ open, setModalOpen, coverImageHref, title, isDeck, children }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <>
      <Dialog
        open={open}
        onClose={() => setModalOpen(false)}
        fullScreen={true}
        sx={{
          ".MuiPaper-root": {
            backdropFilter: "blur(5px)",
            overflow: "hidden",
          },
        }}
      >
        {isDeck ? (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              ".MuiOutlinedInput-root": {
                width: "calc(100% - 3rem)",
                mx: "1.5rem",
                position: "relative",
                top: "5rem",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
              }}
            >
              {children}
            </Box>
            <Box
              sx={{
                ...(isMobile && {
                  transform: "rotate(270deg)",
                  height: "100%",
                  width: "100%",
                  position: "relative",
                  overflowY: "scroll",
                  scrollbarWidth: "thin",
                }),
              }}
            >
              <Box
                id="deck-image"
                sx={{
                  position: "relative",
                  width: { xs: "2200px", sm: "100%" },
                  height: "100vh",
                  m: "auto",
                }}
              >
                <Image
                  src={coverImageHref}
                  alt={title}
                  fill
                  style={{
                    objectFit: "contain",
                  }}
                  loader={({ src }) => src}
                  unoptimized
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100vh",
              "&:active": {
                transform: "scale(2)",
              },
            }}
          >
            <Image
              src={coverImageHref}
              alt={title}
              fill
              style={{ objectFit: "contain" }}
              loader={({ src }) => src}
            />
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 1,
            padding: "4px 4px 1px",
            borderRadius: "5px",
            backdropFilter: "blur(173px)",
            boxShadow: "3px 3px 16px #00000012, -3px -3px 16px #00000012",
            cursor: "pointer",
            svg: {
              fontSize: "30px",
            },
          }}
          onClick={() => {
            setModalOpen((prev) => !prev);
          }}
        >
          <FullscreenExitRoundedIcon />
        </Box>
      </Dialog>
    </>
  );
};

const ShipCard: React.FC<{
  ship: UniqueShip;
  setOpenImage: React.Dispatch<React.SetStateAction<boolean>>;
  setGetImageData: React.Dispatch<
    React.SetStateAction<{
      title: string;
      image: string;
    }>
  >;
  adultOnlyText: (text: string) => "" | "Kids allowed" | "Kids not allowed";
  smokingArea: (
    type: string,
  ) =>
    | ""
    | "Smoking allowed in select areas."
    | "Smoking allowed on balconies and in cabins"
    | "No smoking allowed anywhere onboard.";
}> = ({ ship, setOpenImage, setGetImageData, adultOnlyText, smokingArea }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <Box
      sx={{
        position: "relative",
        border: "2px solid #060273",
        borderRadius: "14px",
        display: { xs: "flex", sm: "block" },
      }}
    >
      <Box
        sx={{
          inset: "0px",
          position: "absolute",
          pointerEvents: "none",
          borderRadius: "14px",
        }}
      ></Box>

      <Box
        sx={{
          position: "relative",
          width: { xs: "50%", sm: "100%" },
          height: "200px",
          borderRadius: isMobile ? "14px 0px 0px 14px" : "14px 14px 0px 0px",
          backgroundImage:
            "radial-gradient(at 100% 18%, rgb(6 2 115 / 9%) 0px, #00000000 50%), radial-gradient(at 40% 0%, rgb(0 0 0 / 31%) 0px, rgb(0 0 0 / 20%) 50%)",
        }}
      >
        {ship?.coverImageHref ? (
          <Image
            src={ship?.coverImageHref ?? ""}
            alt={ship?.title ?? ""}
            fill
            style={{
              borderRadius: isMobile ? "14px 0px 0px 14px" : "14px 14px 0px 0px",
              objectFit: "cover",
            }}
            loader={({ src }) => src}
            unoptimized
            blurDataURL={ship?.coverImageHref ?? ""}
            placeholder="blur"
            priority
          />
        ) : (
          <Image
            src="https://widgety-assets.s3.amazonaws.com/2020/10/20/08/08/28/006b620c-4214-4dd0-8fa8-75ec682e9eef/image coming soon image not available.jpg"
            alt={ship?.title ?? "Cruise ship"}
            fill
            style={{
              borderRadius: "14px 14px 0px 0px",
            }}
            loader={({ src }) => src}
            blurDataURL={ship?.coverImageHref ?? ""}
            placeholder="blur"
            unoptimized
          />
        )}

        <Box
          sx={{
            position: "absolute",
            top: { xs: "none", sm: "10px" },
            bottom: { xs: "10px", sm: "unset" },
            right: "10px",
            zIndex: 1,
            padding: "6px 7px 3px",
            borderRadius: "5px",
            backdropFilter: "blur(173px)",
            boxShadow: "3px 3px 16px #00000012, -3px -3px 16px #00000012",
            cursor: "pointer",
            svg: {
              fontSize: "15px",
            },
          }}
          onClick={() => {
            setOpenImage((prev) => !prev);
            setGetImageData({
              title: ship?.title ?? "",
              image: ship?.coverImageHref ?? "",
            });
          }}
        >
          <OpenInFullRoundedIcon />
        </Box>

        <Typography
          sx={{
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
            fontWeight: 500,
            fontSize: "1.2rem",
            color: " #fff",
            textAlign: "center",
            position: "absolute",
            textShadow: "rgb(71, 35, 217) 1px 1px 3px",
            lineHeight: "16px",
            verticalAlign: "middle",
            left: "10px",
            top: "10px",
          }}
        >
          {toTitleCase(ship?.title ?? "")}
        </Typography>
      </Box>

      <Box
        sx={{
          p: "10px",
          bgcolor: "#E8F2F7",
          position: "relative",
          width: { xs: "50%", sm: "100%" },
          height: "100%",
          minHeight: { xs: "200px", sm: "300px" },
          display: "flex",
          borderRadius: isMobile ? "0px 14px 14px 0px" : "0px 0px 14px 14px",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            minHeight: { sm: "200px" },
            ul: { textAlign: "center" },
            "ul .capacity": {
              bgcolor: "#C6E8F5",
              padding: "7px 10px 6px",
              display: "inline-block",
              borderRadius: "30px",
              mt: "8px",
              mr: "8px",
              fontSize: "12px",
              color: "#1f1f1f",
            },
          }}
        >
          <Grid container size={{ xs: 12 }} spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {ship?.shipFacts?.launchYear && (
                <>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      mb: "0px",
                      textAlign: "left",
                      color: "#1f1f1f",
                    }}
                  >
                    General Info
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "13px",
                      textAlign: "left",
                      color: "#1f1f1f",
                    }}
                  >
                    First setting sail in {ship?.shipFacts?.launchYear}
                  </Typography>
                </>
              )}
              {ship?.shipFacts?.refitYear && (
                <Typography
                  sx={{
                    fontSize: "13px",
                    textAlign: "rigleftht",
                    color: "#1f1f1f",
                  }}
                >
                  Refitted in {ship?.shipFacts?.refitYear}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  mb: "0px",
                  textAlign: "left",
                  color: "#1f1f1f",
                }}
              >
                Facilities
              </Typography>
              {ship?.smoking && (
                <Typography sx={{ fontSize: "13px", textAlign: "left", color: "#1f1f1f" }}>
                  {smokingArea(ship?.smoking)}
                </Typography>
              )}
              {ship?.adultsOnly && (
                <Typography sx={{ fontSize: "13px", textAlign: "left", color: "#1f1f1f" }}>
                  {adultOnlyText(ship?.adultsOnly)}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }} display={{ xs: "none", sm: "block" }}>
              <Box component="ul">
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    mb: "-3px",
                  }}
                >
                  Capacity
                </Typography>
                {(ship?.shipFacts?.crewCount ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Crew: {ship?.shipFacts?.crewCount}
                  </Typography>
                )}
                {(ship?.shipFacts?.deckCount ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Deck: {ship?.shipFacts?.deckCount}
                  </Typography>
                )}
                {(ship?.shipFacts?.cabinCount ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Cabin: {ship?.shipFacts?.cabinCount}
                  </Typography>
                )}
                {(ship?.shipFacts?.largeCabinCount ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Large Cabin: {ship?.shipFacts?.largeCabinCount}
                  </Typography>
                )}
                {(ship?.shipFacts?.wheelchairCabinCount ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Wheelchair Cabin: {ship?.shipFacts?.wheelchairCabinCount}
                  </Typography>
                )}
                {(ship?.shipFacts?.capacity ?? 0) > 0 && (
                  <Typography component="li" className="capacity">
                    Passenger Capacity: {ship?.shipFacts?.capacity}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Link href={`/best-cruise-ships/${ship?.href}`}>
          <Button
            onClick={() => {
              setGetImageData({
                title: ship?.href ?? "",
                image: ship?.coverImageHref ?? "",
              });
            }}
            sx={{
              minWidth: { xs: "100%" },
              width: "fit-content",
              textTransform: "initial",
              p: "7px 16px 5px",
              position: "relative",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
            }}
          >
            View Ship
          </Button>
        </Link>
      </Box>
    </Box>
  );
};

const ShipCardSkeleton = () => {
  return (
    <Box
      sx={{
        position: "relative",
        border: "2px solid #060273",
        borderRadius: "14px",
        display: { xs: "flex", sm: "block" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: { xs: "14px 0px 0px 14px", sm: "14px 14px 0px 0px" },
          width: { xs: "50%", sm: "100%" },
          height: "200px",
        }}
      >
        <Skeleton
          animation="wave"
          variant="rectangular"
          sx={{
            position: "absolute",
            width: "100%",
            height: "200px",
          }}
        />

        <Skeleton
          animation="wave"
          variant="text"
          sx={{
            fontSize: "1.8rem",
            position: "absolute",
            borderRadius: "5px",
            left: "10px",
            width: "200px",
            top: "10px",
          }}
        />

        <Skeleton
          animation="wave"
          variant="rectangular"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 1,
            borderRadius: "5px",
            width: "30px",
            height: "30px",
          }}
        />
      </Box>

      <Box
        sx={{
          p: "10px",
          bgcolor: "#E8F2F7",
          borderRadius: { xs: "0px 14px 14px 0px", sm: "0px 0px 14px 14px" },
          position: "relative",
          width: { xs: "50%", sm: "100%" },
          height: "100%",
          minHeight: { xs: "200px", sm: "300px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Grid container size={{ xs: 12 }} spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Skeleton
              animation="wave"
              variant="text"
              sx={{
                fontSize: "25px",
                fontWeight: 500,
                width: "80%",
              }}
            />
            <Box sx={{ mt: "5px" }}>
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "90%",
                }}
              />
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "60%",
                }}
              />
            </Box>
            <Box sx={{ mt: "5px" }} display={{ xs: "none", sm: "block" }}>
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "90%",
                }}
              />
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "60%",
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Skeleton
              animation="wave"
              variant="text"
              sx={{
                fontSize: "25px",
                fontWeight: 500,
                width: "80%",
              }}
            />
            <Box sx={{ mt: "5px" }}>
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "90%",
                }}
              />
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "60%",
                }}
              />
            </Box>
            <Box sx={{ mt: "5px" }} display={{ xs: "none", sm: "block" }}>
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "90%",
                }}
              />
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "60%",
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }} display={{ xs: "none", sm: "block" }}>
            <Skeleton
              animation="wave"
              variant="text"
              sx={{
                m: "10px auto 0px",
                fontSize: "25px",
                fontWeight: 500,
                width: "80%",
              }}
            />
            <Box
              sx={{
                height: "fit-content",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {[...new Array(5)].map((_, index) => (
                <Skeleton
                  key={index}
                  animation="wave"
                  variant="rectangular"
                  sx={{
                    bgcolor: "#C6E8F5",
                    padding: "7px 10px 6px",
                    borderRadius: "30px",
                    mt: "8px",
                    mr: "8px",
                    height: "30px",
                    width: index % 2 == 1 ? "80px" : "90px",
                    fontSize: "12px",
                    color: "#1f1f1f",
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>

        <Skeleton
          animation="wave"
          variant="rectangular"
          sx={{
            width: "100%",
            height: "30px",
            borderRadius: "8px",
          }}
        />
      </Box>
    </Box>
  );
};

const ShipStyle: React.FC<{ shipStyle: UniqueShip[] }> = ({ shipStyle }) => {
  const [style, setStyle] = useState("");
  useLayoutEffect(() => {
    const getStyle = async () => {
      if (shipStyle[0]?.style === null) {
        try {
          const { data } = await getAllCruisesListV2({ shipName: shipStyle[0]?.href ?? "" });
          setStyle(data?.cruises[0]?.rating);
        } catch (error) {
          console.error(error);
        }
      }
    };
    getStyle();
  }, [shipStyle]);

  return (
    <Typography
      sx={{
        color: "#060273",
        marginTop: "1rem",
        minWidth: "250px",
        textAlign: "center",
        borderRadius: "6px",
        fontFamily: "'blauerNue', 'blauerNue Fallback'",
        fontSize: "1.5rem",
        fontWeight: 500,
        width: "100%",
      }}
    >
      {(shipStyle![0]?.style !== null ? toTitleCase(shipStyle![0]?.style ?? "") : style) +
        " " +
        (shipStyle!.length > 1 ? "Ships" : "Ship")}
    </Typography>
  );
};
